import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { company, contactFaqs } from '../mock';
import Seo from '../components/Seo';
import StickyBookBar from '../components/StickyBookBar';
import { PageFaq } from '../sections/RestSections';
import { useAntiBot, Honeypot } from '../components/useAntiBot';
import TurnstileWidget, { TURNSTILE_ENABLED } from '../components/TurnstileWidget';

const API = process.env.REACT_APP_BACKEND_URL;
const SERVICES = ['General Inquiry', 'Residential Service', 'Commercial Service', 'Water Heater', 'Drain / Clog', 'Water / Gas / Sewer Line', 'Remodeling / Repiping', 'Emergency'];

export default function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: 'General Inquiry', message: '' });
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const { hp, setHp, appendAntiBot } = useAntiBot();
  const [token, setToken] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !token) { setState('error'); return; }
    setState('sending');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      appendAntiBot(fd);
      if (token) fd.append('turnstile_token', token);
      const res = await fetch(`${API}/api/contact`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('bad');
      setState('done');
    } catch (err) {
      setState('error');
    }
  };

  return (
    <main className="page contact-page" data-testid="contact-page">
      <Seo title="Contact Us" description="Request plumbing service in Savannah, GA. Send Victory Plumbing an inquiry and we'll get right back to you." path="/contact" />
      <section className="page-hero">
        <div className="wrap">
          <p className="eyebrow">Get in Touch</p>
          <h1>Request service or an estimate</h1>
          <p className="lede">Tell us what you need and the best way to reach you &mdash; we&rsquo;ll respond promptly during business hours. For urgent issues, please call us directly.</p>
        </div>
      </section>

      <section className="contact-body" id="contact-form">
        <div className="wrap">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Contact details</h2>
              <ul className="contact-list">
                <li><span className="ic-sq"><Phone size={18} /></span><div><strong>Phone</strong><a href={company.phoneHref}>{company.phoneDisplay}</a></div></li>
                <li><span className="ic-sq"><Mail size={18} /></span><div><strong>Email</strong><a href={`mailto:${company.email}`}>{company.email}</a></div></li>
                <li><span className="ic-sq"><MapPin size={18} /></span><div><strong>Location</strong><span>{company.address}</span></div></li>
                <li><span className="ic-sq"><Clock size={18} /></span><div><strong>Hours</strong><span>{company.hours}</span></div></li>
              </ul>
            </div>

            <div className="contact-form-wrap">
              {state === 'done' ? (
                <div className="contact-success" data-testid="contact-success">
                  <span className="ic-sq"><CheckCircle2 size={26} /></span>
                  <h3>Thanks &mdash; we got your message!</h3>
                  <p>We&rsquo;ll be in touch shortly. Need us right away? Call <a href={company.phoneHref}>{company.phoneDisplay}</a>.</p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={submit} data-testid="contact-form">
                  <Honeypot value={hp} onChange={setHp} />
                  <h2>Send us a message</h2>
                  <div className="cf-row">
                    <label>Name*<input required value={form.name} onChange={set('name')} data-testid="contact-name" /></label>
                    <label>Phone<input value={form.phone} onChange={set('phone')} data-testid="contact-phone" /></label>
                  </div>
                  <label>Email*<input type="email" required value={form.email} onChange={set('email')} data-testid="contact-email" /></label>
                  <label>What do you need?
                    <select value={form.service} onChange={set('service')} data-testid="contact-service">
                      {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label>Message*<textarea required rows={5} value={form.message} onChange={set('message')} data-testid="contact-message" /></label>
                  {state === 'error' && <p className="cf-error" data-testid="contact-error">Please complete the verification below, or call us at {company.phoneDisplay}.</p>}
                  <TurnstileWidget onToken={setToken} />
                  <button className="btn btn-gold" type="submit" disabled={state === 'sending'} data-testid="contact-submit">
                    {state === 'sending' ? <><Loader2 size={16} className="spin" /> Sending&hellip;</> : <><Mail size={16} /> Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <PageFaq title="Contact FAQs" subtitle="Quick answers before you reach out." items={contactFaqs} testid="contact-faqs" />
      <StickyBookBar bookHref="#contact-form" bookLabel="Send Message" />
    </main>
  );
}
